/* eslint-disable no-param-reassign */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, Fragment, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import Helpers from '@Apis/helpers';
import AnalyticsActions from '@Actions/application/analyticsActions';
import H3DateSelector from '@Uilib/h3DateSelector';
import H3Text from '@Uilib/h3Text';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import TenantSelector from '@Components/Common/Selector/TenantSelector';
import { Table, Tooltip } from 'antd';
import MetricWithStatusSplit from '@Components/Home/HomeTabs/Widgets/MetricWithStatusSplit';
import './style.scss';

import MetricView from '../Widgets/MetricView';
import MetricBarLineChart from '../Widgets/MetricBarLineChart';
import { dateRangePresets, dateRangePresetsValues, entityNameEnum, filterTypeEnums } from '../../../../apis/constants';
import useCheckPortrait from '../../../../hooks/useCheckPortrait';
import UserSavedFilterViewSelector from '../../../Common/UserSavedFilterViewSelector';

const ProductionDashboard = ({
  user, history, getAnalyticsData, analytics,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedInterval, setSelectedInterval] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTenant, setSelectedTenant] = useState([]);
  const [costingMethod, setCostingMethod] = useState('');
  const [isUserReady, setIsUserReady] = useState(false);

  const [currentDefaultView, setCurrentDefaultView] = useState({});
  const [currentlyAppliedFilters, setCurrentlyAppliedFilters] = useState([]);
  const [selectedFilterView, setSelectedFilterView] = useState({});
  const [isUserReadyInitial, setIsUserReadyInitial] = useState(false);
  const [isUpdateFilterSate, setIsUpdateFilterSate] = useState(true);
  const [savedFilterViews, setSavedFilterViews] = useState([]);
  const [savedFilterViewsLoading, setSavedFilterViewsLoading] = useState(false);

  const [orderDateSelectedIntervalDateRangeType, setOrderDateSelectedIntervalDateRangeType] = useState('');

  const {
    isMobile, isPortrait, isMobileAndPortrait, isMobileOrPortrait,
  } = useCheckPortrait();

  function getFilterValueByKey({ filtersArray, key, valueOnly = true }) {
    const selectedFilter = filtersArray.find((item) => item.filter_key === key) || null;
    return valueOnly ? selectedFilter?.filter_value : selectedFilter;
  }

  function getAppliedFiltersFromURL() {
    const appliedFiltersFromURL = [
      {
        filter_key: 'tenant',
        filter_value: Helpers.getValueFromUrl('tenant') ? Helpers.getValueFromUrl('tenant')?.split(',')?.map((item) => (parseInt(item))) : [],
        filter_type: filterTypeEnums.ARRAY,
      },
      {
        filter_key: 'startDate',
        filter_value: Number(Helpers.getValueFromUrl('startDate')) || '',
        filter_type: filterTypeEnums.DATE_RANGE,
        date_range_type: Helpers.getValueFromUrl('order_date_type') || '',
      },
      {
        filter_key: 'endDate',
        filter_value: Number(Helpers.getValueFromUrl('endDate')) || '',
        filter_type: filterTypeEnums.DATE_RANGE,
        date_range_type: Helpers.getValueFromUrl('order_date_type') || '',
      },
      {
        filter_key: 'costingMethod',
        filter_value: Helpers.getValueFromUrl('costingMethod'),
        filter_type: filterTypeEnums.STRING,
      },
    ];
    return appliedFiltersFromURL;
  }

  const initialFilterValues = useMemo(() => [
    {
      filter_key: 'tenant',
      filter_value: Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.MO, Helpers.permissionTypes.READ),
      filter_type: filterTypeEnums.ARRAY,
    },
    {
      filter_key: 'startDate',
      filter_value: dateRangePresetsValues(dateRangePresets.THIS_MONTH)[0],
      filter_type: filterTypeEnums.DATE_RANGE,
      date_range_type: dateRangePresets.THIS_MONTH,
    },
    {
      filter_key: 'endDate',
      filter_value: dateRangePresetsValues(dateRangePresets.THIS_MONTH)[1],
      filter_type: filterTypeEnums.DATE_RANGE,
      date_range_type: dateRangePresets.THIS_MONTH,
    },
    {
      filter_key: 'costingMethod',
      filter_value: 'cost_price',
      filter_type: filterTypeEnums.STRING,
    },
  ], []);

  const systemDefaultUserView = useMemo(() => ({
    filter_id: 'system_default',
    filter_name: 'System Default View',
    entity_name: entityNameEnum.PRODUCTION_DASHBOARD,
    filters: initialFilterValues,
    tenant_id: user?.tenant_info?.tenant_id,
    org_id: user?.user_org_id,
    user_id: user?.user_id,
    is_default: false,
    is_system_default: true,
  }), []);

  const productColumns = [
    {
      title: 'Product Name',
      render: (text, record) => (
        <Tooltip title={`${record?.internal_sku_code} - ${record?.product_sku_name}`} className="">
          <a target="_blank" href={`/inventory/product/view/${record.product_sku_id}`} rel="noreferrer">
            <div>{`${record?.internal_sku_code} - ${record?.product_sku_name}`.trim()?.length > 70 ? `${`${record?.internal_sku_code} - ${record?.product_sku_name}`.trim()?.substring(0, 70)}..` : `${record?.internal_sku_code} - ${record?.product_sku_name}`}</div>
          </a>
        </Tooltip>
      ),
    },
    {
      title: 'Sent for Production',
      render: (text, record) => <H3Text text={(Number(record?.ordered_quantity)?.toFixed(2))} className="" />,
      sorter: (a, b) => Number(a.ordered_quantity) - Number(b.ordered_quantity),
    },
    {
      title: 'Pending',
      render: (text, record) => <H3Text text={(Number(record?.pending_quantity)?.toFixed(2))} className="" />,
      sorter: (a, b) => Number(a.pending_quantity) - Number(b.pending_quantity),
    },
    {
      title: 'Completed',
      render: (text, record) => <H3Text text={(Number(record?.completed_quantity)?.toFixed(2))} className="" />,
      sorter: (a, b) => Number(a.completed_quantity) - Number(b.completed_quantity),
    },
    {
      title: 'Rejected',
      render: (text, record) => <H3Text text={(Number(record?.scraped_quantity)?.toFixed(2))} className="" />,
      sorter: (a, b) => Number(a.scraped_quantity) - Number(b.scraped_quantity),
    },
  ];

  function convertTableToCSV(headers, data) {
    const header = headers;
    const rows = data;
    const csv = [header, ...rows].map((row) => row.join(',')).join('\n');
    return csv;
  }

  function downloadCSV(csvContent, fileName) {
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = fileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  const handleDownloadProduction = (dataSource) => {
    const headers = ['Internal Sku Code', 'Product Name', 'Orders Received', 'Pending', 'Completed', 'Rejected'];
    const data = dataSource.map((d) => {
      const productData = [];
      productData.push(`"${d.internal_sku_code}"`);
      productData.push(`"${d.product_sku_name}"`);
      productData.push(Number(d.ordered_quantity || 0)?.toFixed(2));
      productData.push(Number(d.pending_quantity || 0)?.toFixed(2));
      productData.push(Number(d.completed_quantity || 0)?.toFixed(2));
      productData.push(Number(d.scraped_quantity || 0)?.toFixed(2));
      return productData;
    });
    const csvContent = convertTableToCSV(headers, data);
    downloadCSV(csvContent, 'production_by_product.csv');
  };

  const pushParamsToUrl = (tenant, start, end, method, orderDateSelectedIntervalDateRangeType, selectedFilterView) => {
    let url = '?tab=/production-dashboard&';
    if (tenant) url += `tenant=${tenant}`;
    if (start) url += `&startDate=${start}`;
    if (end) url += `&endDate=${end}`;
    if (method) url += `&costingMethod=${method}`;
    if (orderDateSelectedIntervalDateRangeType) url += `&order_date_type=${orderDateSelectedIntervalDateRangeType}`;
    if (selectedFilterView) url += `&filter_id=${selectedFilterView?.filter_id}`;
    history.push(url);
  };

  useEffect(() => {
    if (user?.tenant_info?.org_id) {
      // setCurrentPage(Number(Helpers.getValueFromUrl('page') || currentPage));
      // setStartDate(Number(Helpers.getValueFromUrl('start')) || dayjs().utc().startOf('month').valueOf());
      // setEndDate(Number(Helpers.getValueFromUrl('end')) || dayjs().utc().endOf('month').subtract(1, 'day')
      //   .valueOf());
      // setSelectedTenant(Helpers.getValueFromUrl('tenant') ? Helpers.getValueFromUrl('tenant')?.split(',')?.map((item) => (parseInt(item))) : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.MO, Helpers.permissionTypes.READ));
      // setIsUserReady(true);
      // setCostingMethod(Number(Helpers.getValueFromUrl('costingMethod')) || 'cost_price');
      setSelectedFilterView(systemDefaultUserView);
    }
  }, []);

  useEffect(() => {
    if (savedFilterViews && isUserReadyInitial) {
      const defaultUserView = savedFilterViews.find((item) => item.is_default);

      if (defaultUserView) {
        setCurrentDefaultView(defaultUserView);
      } else {
        setCurrentDefaultView(systemDefaultUserView);
      }

      const appliedFiltersFromURL = getAppliedFiltersFromURL();

      const selectedFilterViewFromURL = Helpers.getValueFromUrl('filter_id');

      let currentSelectedView;
      if (selectedFilterViewFromURL) {
        currentSelectedView = savedFilterViews.find((item) => String(item.filter_id) === String(selectedFilterViewFromURL));
        if (!currentSelectedView) {
          currentSelectedView = systemDefaultUserView;
        }
      } else {
        currentSelectedView = defaultUserView || systemDefaultUserView;
      }

      const filterValues = (isFromURL) => {
        const currentSelectedViewFilters = currentSelectedView.filters;

        const filterValue = initialFilterValues.map((item) => item.filter_key).reduce((acc, filterKey) => ({ ...acc, [filterKey]: getFilterValueByKey({ filtersArray: isFromURL ? appliedFiltersFromURL : currentSelectedViewFilters, key: filterKey }) }), {});

        const _orderDateRangeType = getFilterValueByKey({ filtersArray: isFromURL ? appliedFiltersFromURL : currentSelectedViewFilters, key: 'startDate', valueOnly: false }).date_range_type;

        let _orderStartDate = '';
        let _orderEndDate = '';

        if (_orderDateRangeType === '' || _orderDateRangeType === dateRangePresets.CUSTOM) {
          // No action needed for empty or custom date range type
        } else {
          const date = dateRangePresetsValues(_orderDateRangeType);
          const [start, end] = date;

          _orderStartDate = start;
          _orderEndDate = end;
          filterValue.startDate = _orderStartDate;
          filterValue.endDate = _orderEndDate;
        }

        filterValue.orderDateRangeType = _orderDateRangeType;

        return filterValue;
      };

      const tempFilterValues = filterValues(selectedFilterViewFromURL);

      setSelectedFilterView(currentSelectedView);
      if (isUpdateFilterSate) {
        setCurrentlyAppliedFilters(appliedFiltersFromURL);

        setCurrentPage(Number(Helpers.getValueFromUrl('page') || currentPage));
        setSelectedTenant(tempFilterValues.tenant);
        setStartDate(tempFilterValues.startDate);
        setEndDate(tempFilterValues.endDate);
        setOrderDateSelectedIntervalDateRangeType(tempFilterValues.orderDateRangeType);
        setCostingMethod(tempFilterValues.costingMethod);
      }

      if (!isUserReady) setIsUserReady(true);
      if (!isUpdateFilterSate) setIsUpdateFilterSate(true);
    }
  }, [savedFilterViews, isUserReadyInitial]);

  /**
   *
   */
  useEffect(() => {
    if (isUserReady) {
      pushParamsToUrl(selectedTenant, startDate, endDate, costingMethod, orderDateSelectedIntervalDateRangeType, selectedFilterView);
      setCurrentlyAppliedFilters(getAppliedFiltersFromURL());
      getAnalyticsData('PRODUCTION', 'PRODUCTION_OUTPUT', {
        tenantIds: selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.MO, Helpers.permissionTypes.READ)?.join(','),
        startDate: dayjs(startDate).format('YYYY-MM-DD'),
        endDate: dayjs(endDate).format('YYYY-MM-DD'),
      });
      getAnalyticsData('PRODUCTION', 'PRODUCTION_DELIVERY_TIMELINESS', {
        tenantIds: selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.MO, Helpers.permissionTypes.READ)?.join(','),
        startDate: dayjs(startDate).format('YYYY-MM-DD'),
        endDate: dayjs(endDate).format('YYYY-MM-DD'),
      });
      getAnalyticsData('PRODUCTION', 'PRODUCTION_BY_STATUS', {
        tenantIds: selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.MO, Helpers.permissionTypes.READ)?.join(','),
        startDate: dayjs(startDate).format('YYYY-MM-DD'),
        endDate: dayjs(endDate).format('YYYY-MM-DD'),
      });
      getAnalyticsData('PRODUCTION', 'PRODUCTION_TREND', {
        tenantIds: selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.MO, Helpers.permissionTypes.READ)?.join(','),
        startDate: dayjs(startDate).format('YYYY-MM-DD'),
        endDate: dayjs(endDate).format('YYYY-MM-DD'),
      });
      getAnalyticsData('PRODUCTION', 'PRODUCTION_PRODUCT_WISE', {
        tenantIds: selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.MO, Helpers.permissionTypes.READ)?.join(','),
        startDate: dayjs(startDate).format('YYYY-MM-DD'),
        endDate: dayjs(endDate).format('YYYY-MM-DD'),
      });
    }
  }, [selectedTenant, startDate, endDate, costingMethod, orderDateSelectedIntervalDateRangeType]);

  /**
   *
   * @returns {{count: number, list: ({color: string, count: number, label: string, value: number}|{color: string, count: number, label: string, value: number}|{color: string, count: number, label: string, value: number})[], value: number, entity: string}}
   */
  const getStatusSplitData = () => {
    const data = analytics?.PRODUCTION?.PRODUCTION_BY_STATUS?.data;
    let totalCount = 0;
    let totalValue = 0;
    const confirmed = {
      count: 0,
      value: 0,
      label: 'Confirmed',
      color: 'rgb(34, 197, 94)',
    };
    const inProgress = {
      count: 0,
      value: 0,
      label: 'In Progress',
      color: 'rgb(255, 171, 0)',
    };
    const draft = {
      count: 0,
      value: 0,
      label: 'Draft',
      color: '#1c7be1',
    };
    const closed = {
      count: 0,
      value: 0,
      label: 'Completed',
      color: '#1c7be1',
    };
    const cancel = {
      count: 0,
      value: 0,
      label: 'Void',
      color: 'rgb(255, 86, 48)',
    };
    for (let i = 0; i < data?.length; i++) {
      if (data[i]?.status === 'CONFIRMED') {
        confirmed.count += Number(data[i]?.no_of_orders);
        confirmed.value += Number(data[i]?.no_of_orders);
        // confirmed.value += Number(data[i]?.fg_value);
        totalCount += Number(data[i]?.no_of_orders);
        totalValue += Number(data[i]?.fg_value);
      }
      if (data[i]?.status === 'IN_PROGRESS') {
        inProgress.count += Number(data[i]?.no_of_orders);
        inProgress.value += Number(data[i]?.no_of_orders);
        // inProgress.value += Number(data[i]?.fg_value);
        totalCount += Number(data[i]?.no_of_orders);
        totalValue += Number(data[i]?.fg_value);
      }
      if (data[i]?.status === 'DRAFT') {
        draft.count += Number(data[i]?.no_of_orders);
        draft.value += Number(data[i]?.no_of_orders);
        // draft.value += Number(data[i]?.fg_value);
        totalCount += Number(data[i]?.no_of_orders);
        totalValue += Number(data[i]?.fg_value);
      }
      if (data[i]?.status === 'COMPLETED') {
        closed.count += Number(data[i]?.no_of_orders);
        closed.value += Number(data[i]?.no_of_orders);
        // closed.value += Number(data[i]?.fg_value);
        totalCount += Number(data[i]?.no_of_orders);
        totalValue += Number(data[i]?.fg_value);
      }
      if (data[i]?.status === 'VOID') {
        cancel.count += Number(data[i]?.no_of_orders);
        cancel.value += Number(data[i]?.no_of_orders);
        // cancel.value += Number(data[i]?.fg_value);
        totalCount += Number(data[i]?.no_of_orders);
        totalValue += Number(data[i]?.fg_value);
      }
    }
    return {
      count: totalCount,
      value: totalCount,
      entity: 'Manufacturing Orders',
      list: [
        {
          ...confirmed,
          url: `/production/manufacturing-orders?page=1&limit=10&tenant=${selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.MO, Helpers.permissionTypes.READ)?.join(',')}&startDate=${startDate}&endDate=${endDate}&status=CONFIRMED`,
        }, {
          ...inProgress,
          url: `/production/manufacturing-orders?page=1&limit=10&tenant=${selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.MO, Helpers.permissionTypes.READ)?.join(',')}&startDate=${startDate}&endDate=${endDate}&status=IN_PROGRESS`,
        }, {
          ...draft,
          url: `/production/manufacturing-orders?page=1&limit=10&tenant=${selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.MO, Helpers.permissionTypes.READ)?.join(',')}&startDate=${startDate}&endDate=${endDate}&status=DRAFT`,
        },
        {
          ...closed,
          url: `/production/manufacturing-orders?page=1&limit=10&tenant=${selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.MO, Helpers.permissionTypes.READ)?.join(',')}&startDate=${startDate}&endDate=${endDate}&status=COMPLETED`,
        },
        {
          ...cancel,
          url: `/production/manufacturing-orders?page=1&limit=10&tenant=${selectedTenant?.filter((item) => !isNaN(item))?.length > 0 ? selectedTenant?.join(',') : Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.MO, Helpers.permissionTypes.READ)?.join(',')}&startDate=${startDate}&endDate=${endDate}&status=VOID`,
        },
      ].map((item) => ({
        ...item,
        url: Helpers.appendSystemDefaultFilterIdInURL(item?.url),
      })),
    };
  };

  return (
    <Fragment>
      <div className="dashboard-filters doc-list__wrapper doc-list__tab-content">
        <div className="flex-display" style={{ gap: '10px' }}>
          <UserSavedFilterViewSelector
            selectedFilterView={selectedFilterView}
            onChangeTab={(currentSelectedView) => {
              setSelectedFilterView(() => {
                const currentSelectedViewFilters = currentSelectedView.filters;

                const filterValue = initialFilterValues.map((item) => item.filter_key).reduce((acc, filterKey) => ({ ...acc, [filterKey]: getFilterValueByKey({ filtersArray: currentSelectedViewFilters, key: filterKey }) }), {});

                const _orderDateRangeType = getFilterValueByKey({ filtersArray: currentSelectedViewFilters, key: 'startDate', valueOnly: false }).date_range_type;

                let _orderStartDate = '';
                let _orderEndDate = '';

                if (_orderDateRangeType === '' || _orderDateRangeType === dateRangePresets.CUSTOM) {
                  // No action needed for empty or custom date range type
                } else {
                  const date = dateRangePresetsValues(_orderDateRangeType);
                  const [start, end] = date;

                  _orderStartDate = start;
                  _orderEndDate = end;
                  filterValue.startDate = _orderStartDate;
                  filterValue.endDate = _orderEndDate;
                }

                filterValue.orderDateRangeType = _orderDateRangeType;

                const tempFilterValues = filterValue;
                setCurrentPage(1);

                setCurrentPage(Number(Helpers.getValueFromUrl('page') || currentPage));
                setSelectedTenant(tempFilterValues.tenant);
                setStartDate(tempFilterValues.startDate);
                setEndDate(tempFilterValues.endDate);
                setOrderDateSelectedIntervalDateRangeType(tempFilterValues.orderDateRangeType);
                setCostingMethod(tempFilterValues.costingMethod);

                return currentSelectedView;
              });
            }}
            currentlyAppliedFilters={currentlyAppliedFilters}
            entityName={entityNameEnum.PRODUCTION_DASHBOARD}
            setSavedFilterViews={(value) => setSavedFilterViews(value)}
            setSavedFilterViewsLoading={(value) => setSavedFilterViewsLoading(value)}
            setIsUpdateFilterSate={(value) => setIsUpdateFilterSate(value)}
            callback={() => {
              if (!isUserReadyInitial) setIsUserReadyInitial(true);
            }}
            systemDefaultUserView={systemDefaultUserView}
          />
          <div className="flex-display">
            <TenantSelector
              selectedTenant={selectedTenant}
              showSearch
              showAll={false}
              noDropdownAlign
              isMultiple
              onChange={(value) => {
                setSelectedTenant(value);
              }}
              placeholder="Select Business Unit"
              inputClassName="orgInputContainer"
              hideTitle
              includedTenants={Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.MO, Helpers.permissionTypes.READ)}
            />
            <H3DateSelector
              selectedInterval={orderDateSelectedIntervalDateRangeType}
              startDate={startDate}
              endDate={endDate}
              // onDateChange={(date) => {
              //   const getFormattedDate = (d) => (dayjs(new Date(d).toISOString()?.split('T')?.[0]));
              //   setStartDate(date[0]?.valueOf());
              //   setEndDate(date[1]?.valueOf());
              //   setSelectedInterval('customize');
              //   pushParamsToUrl(selectedTenant, getFormattedDate(date[0]?.valueOf()), getFormattedDate(date[1]?.valueOf()));
              // }}
              onDateChangeWithDateRangeType={({ date, dateRangeType }) => {
                setStartDate(dayjs(date[0])?.startOf('day').valueOf());
                setEndDate(dayjs(date[1])?.endOf('day').valueOf());
                setCurrentPage(1);
                setOrderDateSelectedIntervalDateRangeType(dateRangeType);
              }}
            />
          </div>
        </div>
      </div>
      <div className="ant-row production_dashboard__wrapper production_dashboard__wrapper_mobile_view" style={{ marginTop: '20px', width: 'calc(100% + 10px)' }}>
        <div className="ant-col-md-4">
          <MetricView data={analytics.PRODUCTION.PRODUCTION_OUTPUT.data} loading={analytics.PRODUCTION.PRODUCTION_OUTPUT.loading} value={parseInt(analytics.PRODUCTION.PRODUCTION_OUTPUT.data?.avg_daily_output || 0)} title="Average Daily Output" type="AMOUNT" helpText={['This metric shows the average value of your production output per day over the selected timeframe.', "It's calculated by dividing the total production output by the number of days in the period."]} />
        </div>
        <div className="ant-col-md-4">
          <MetricView data={analytics.PRODUCTION.PRODUCTION_OUTPUT.data} loading={analytics.PRODUCTION.PRODUCTION_OUTPUT.loading} value={parseInt(analytics.PRODUCTION.PRODUCTION_OUTPUT.data?.yield || 0)} title="Units Produced" type="NUMBER" helpText={['This metric represents the total number of units produced during the selected timeframe.']} />
        </div>
        <div className="ant-col-md-4">
          <MetricView data={analytics.PRODUCTION.PRODUCTION_OUTPUT.data} loading={analytics.PRODUCTION.PRODUCTION_OUTPUT.loading} value={parseInt(analytics.PRODUCTION.PRODUCTION_OUTPUT.data?.total_output || 0)} title="Production Output" type="AMOUNT" helpText={['This metric shows the total monetary value of your production output during the selected timeframe.']} />
        </div>
        <div className="ant-col-md-4">
          <MetricView data={analytics.PRODUCTION.PRODUCTION_OUTPUT.data} loading={analytics.PRODUCTION.PRODUCTION_OUTPUT.loading} value={parseInt(analytics.PRODUCTION.PRODUCTION_OUTPUT.data?.scrap_output || 0)} title="Scrap Produced" type="AMOUNT" helpText={['This metric represents the total monetary value of any materials or products that were wasted during the production process.']} />
        </div>
        <div className="ant-col-md-4">
          <MetricView
            data={analytics.PRODUCTION.PRODUCTION_OUTPUT.data}
            loading={analytics.PRODUCTION.PRODUCTION_OUTPUT.loading}
            value={parseInt(analytics.PRODUCTION.PRODUCTION_OUTPUT.data?.yield_percentage || 0)}
            title="Yield"
            type="PERCENTAGE"
            helpText={['This metric indicates the percentage of usable output compared to the total finished goods produced.',
              'A higher yield signifies a more efficient production process.',
              "It's calculated by dividing the Production Output by the sum of Production Output and Scrap Produced, then multiplying by 100%."]}
          />
        </div>
        <div className="ant-col-md-4">
          <MetricView
            data={analytics.PRODUCTION.PRODUCTION_DELIVERY_TIMELINESS.data}
            loading={analytics.PRODUCTION.PRODUCTION_DELIVERY_TIMELINESS.loading}
            value={parseFloat(analytics.PRODUCTION.PRODUCTION_DELIVERY_TIMELINESS.data?.timeliness_percentage || 0)?.toFixed(2)}
            title="On Time Production"
            type="PERCENTAGE"
            helpText={['This metric shows the percentage of your production that was completed on time according to your schedule.',
              'This metric can be helpful in identifying potential bottlenecks or delays in your production process.']}
          />
        </div>
      </div>
      <div className="ant-row production_dashboard__wrapper" style={{ marginTop: '10px', width: 'calc(100% + 10px)' }}>
        <div className="ant-col-md-8 ant-col-xs-24">
          <MetricWithStatusSplit data={getStatusSplitData()} loading={analytics?.PRODUCTION?.PRODUCTION_BY_STATUS?.loading} />
        </div>
        <div className="ant-col-md-16" style={{ width: 'calc(66.64% - 10px)', marginRight: '10px' }}>
          <MetricBarLineChart
            data={analytics.PRODUCTION.PRODUCTION_TREND.data || []}
            loading={analytics.PRODUCTION.PRODUCTION_TREND.loading}
            title="Production Trend"
            columnTitle="Production Output"
            lineTitle="Production Units"
            columnDataKey="total_units_cost"
            lineDataKey="total_units_count"
            dateKey="production_date"
            chartSize={300}
          />
        </div>
      </div>
      <div className="ant-row production_dashboard__wrapper" style={{ marginTop: '10px', width: 'calc(100% + 10px)' }}>
        <div className="ant-col-md-24" style={{ width: '100%' }}>
          <div className="production-dashboard-table__wrapper">
            <div className="production-dashboard-table__title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <H3Text text="Product Wise Production" />
              <div className="production-dashboard-table__action-btn" onClick={() => handleDownloadProduction(analytics.PRODUCTION.PRODUCTION_PRODUCT_WISE.data)}>
                <H3Text
                  text={(
                    <FontAwesomeIcon icon={faDownload} color="rgb(0,143,251)" style={{ cursor: 'pointer' }} />
                  )}
                />
              </div>
            </div>
            <div className="production-dashboard-table__content">
              <div className="production-dashboard-table__chart">
                <Table
                  showHeader
                  size="small"
                  scroll="scroll"
                  columns={productColumns}
                  dataSource={analytics.PRODUCTION.PRODUCTION_PRODUCT_WISE.data}
                  loading={analytics.PRODUCTION.PRODUCTION_PRODUCT_WISE.loading}
                  pagination={
                    {
                      pageSize: 5,
                      showSizeChanger: false,
                    }
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = ({ UserReducers, AnalyticsReducers }) => ({
  user: UserReducers.user,
  analytics: AnalyticsReducers.analytics,
});

const mapDispatchToProps = (dispatch) => ({
  getAnalyticsData: (dataType, name, params) => dispatch(AnalyticsActions.getAnalyticsData(dataType, name, params)),
});

ProductionDashboard.propTypes = {
  user: PropTypes.any,
  history: PropTypes.func,
  analytics: PropTypes.any,
  getAnalyticsData: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ProductionDashboard));
